import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { movementsApi, scopeParams, type StoreScopeParams } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { MovementDayCell, MovementKind, MovementsDayGridDay } from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { useStickyTable } from '../../context/StickyTableContext';
import { formatKopeks } from '../../lib/money';
import type { MovementCellRef } from './MovementsDayDrawer';

interface GridCol {
  key: string;
  title: string;
  render: (row: MovementsDayGridDay) => JSX.Element;
  total: JSX.Element;
  fixed?: 'left';
  width?: number;
  onCell?: (row: MovementsDayGridDay) => { onClick?: () => void; style?: React.CSSProperties };
}

/** Σ sklad price for a store-day, «—» when nothing left; a «!» flags lines
 * without a price (they add 0, so «—» would otherwise read as «nothing»). */
function CellValue({ cell }: { cell?: MovementDayCell }): JSX.Element {
  const { t } = useTranslation();
  if (!cell || cell.count === 0) return <Typography.Text type="secondary">—</Typography.Text>;
  const sum = cell.buySumKopeks ? (
    <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {formatKopeks(cell.buySumKopeks)}
    </span>
  ) : (
    <Typography.Text type="secondary">—</Typography.Text>
  );
  if (cell.unpricedCount === 0) return sum;
  return (
    <Tooltip title={t('movements.unpriced', { count: cell.unpricedCount })}>
      <span style={{ whiteSpace: 'nowrap' }}>
        {sum}
        <Typography.Text type="warning" strong style={{ marginInlineStart: 4 }}>
          !
        </Typography.Text>
      </span>
    </Tooltip>
  );
}

/**
 * «Перемещения» jadval: rows = days with activity, one column per store in
 * scope, cell = Σ sklad price of goods that left the store that day; a tap on
 * a non-empty cell opens the drawer with the movements behind it.
 */
export function MovementsDayGridTable({
  month,
  kind,
  scope,
  onCellClick,
}: {
  month: string;
  kind: MovementKind | 'ALL';
  scope: StoreScopeParams;
  onCellClick: (ref: MovementCellRef) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();

  const query = useQuery({
    queryKey: queryKeys.movementsDayGrid(month, kind, scope),
    queryFn: () =>
      movementsApi.dayGrid({
        month,
        ...(kind === 'ALL' ? {} : { kind }),
        ...scopeParams(scope),
      }),
    placeholderData: keepPreviousData,
  });

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const grid = query.data;
  if (grid.grand.count === 0) {
    return <EmptyBlock description={t('movements.empty')} hint={t('movements.emptyHint')} />;
  }
  const today = dayjs().format('YYYY-MM-DD');

  const cols: GridCol[] = [
    {
      key: 'date',
      title: t('common.date'),
      fixed: 'left',
      width: 62,
      render: (row) => {
        const d = dayjs(row.date);
        const weekend = d.day() === 0 || d.day() === 6;
        return (
          <span
            style={{ fontVariantNumeric: 'tabular-nums', color: weekend ? '#cf1322' : undefined }}
          >
            {d.format('DD.MM')}
          </span>
        );
      },
      total: <strong>{t('daygrid.total')}</strong>,
    },
    ...grid.stores.map<GridCol>((s) => ({
      key: `store-${s.id}`,
      title: s.name,
      render: (row) => <CellValue cell={row.byStore[s.id]} />,
      total: <CellValue cell={grid.totals[s.id]} />,
      onCell: (row) =>
        row.byStore[s.id]
          ? {
              onClick: () => onCellClick({ date: row.date, storeId: s.id, storeName: s.name }),
              style: { cursor: 'pointer' },
            }
          : {},
    })),
    ...(grid.stores.length > 1
      ? [
          {
            key: 'total',
            title: t('daygrid.total'),
            render: (row: MovementsDayGridDay) => <CellValue cell={row.total} />,
            total: <CellValue cell={grid.grand} />,
          } satisfies GridCol,
        ]
      : []),
  ];

  const columns: ColumnsType<MovementsDayGridDay> = cols.map((c) => ({
    key: c.key,
    title: c.title,
    fixed: c.fixed,
    width: c.width,
    align: c.key === 'date' ? 'left' : 'right',
    render: (_: unknown, row: MovementsDayGridDay) => c.render(row),
    onCell: c.onCell ? (row: MovementsDayGridDay) => c.onCell!(row) : undefined,
  }));

  return (
    <Table<MovementsDayGridDay>
      dataSource={grid.days}
      columns={columns}
      rowKey="date"
      size="small"
      bordered
      pagination={false}
      sticky={sticky}
      scroll={{ x: 'max-content' }}
      rowClassName={(row) => (row.date === today ? 'jadval-today' : '')}
      summary={() => (
        <Table.Summary>
          <Table.Summary.Row style={{ fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
            {cols.map((c, i) => (
              <Table.Summary.Cell key={c.key} index={i} align={c.key === 'date' ? 'left' : 'right'}>
                {c.total}
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
}
